import type {
  Answer,
  BinaryQuestion,
  MultipartQuestion,
  MultipleChoiceQuestion,
  Question,
  SingleChoiceQuestion,
} from '@front-end/ngx-interview';

export interface QuestionnaireOfferOptionData {
  value: number;
  variant: 'positive' | 'negative' | 'neutral';
  text: string;
}

export interface OfferSpecificValues {
  variable: QuestionnaireOfferOptionData;
  fixed1Year: QuestionnaireOfferOptionData;
  fixed3Years: QuestionnaireOfferOptionData;
  dynamic: QuestionnaireOfferOptionData;
}

export type QuestionnaireOfferValues = keyof OfferSpecificValues;

export interface RetentionAnswer {
  text: string;
  answer: OfferSpecificValues;
  subtitle?: string;
  questionKey: string;
}

export interface RetentionQuestion {
  key: Question['key'];
  text: Question['text'];
  kind: Question['kind'];
  options: RetentionAnswer[];
}

const QUESTION_ENERGY_COSTS: RetentionQuestion = {
  key: 'energyCosts',
  text: 'Hoe kijk je naar je energiekosten?',
  kind: 'singleChoice',
  options: [
    {
      text: 'Ik wil voor een aantal jaar precies weten wat ik betaal.',
      questionKey: 'fixedMultipleYears',
      answer: {
        variable: {
          value: -6,
          variant: 'negative',
          text: 'Je wilt voor een aantal jaar precies weten wat je betaalt, terwijl bij een variabel contract de prijzen elk kwartaal kunnen veranderen.',
        },
        fixed1Year: {
          value: 3,
          variant: 'neutral',
          text: 'Je wilt voor een aantal jaar precies weten wat je betaalt. Een 1-jarig contract geeft wel zekerheid, maar niet voor meerdere jaren.',
        },
        fixed3Years: {
          value: 7.5,
          variant: 'positive',
          text: 'Je wilt voor een aantal jaar precies weten wat je betaalt. Dat past perfect bij een 3-jarig vast contract.',
        },
        dynamic: {
          value: -7.5,
          variant: 'negative',
          text: 'Je wilt voor een aantal jaar precies weten wat je betaalt, terwijl bij een dynamisch contract de prijzen elk uur kunnen veranderen.',
        },
      },
    },
    {
      text: 'Ik wil elk jaar weten wat ik voor dat jaar betaal. ',
      questionKey: 'fixedSingleYear',
      answer: {
        variable: {
          value: -1.5,
          variant: 'negative',
          text: 'Je wilt elk een jaar weten wat je betaalt, terwijl variabele tarieven per kwartaal kunnen veranderen.',
        },
        fixed1Year: {
          value: 7.5,
          variant: 'positive',
          text: 'Je wilt elk jaar weten wat je betaalt en daarna opnieuw kunnen kiezen. Perfect bij een 1-jarig contract.',
        },
        fixed3Years: {
          value: 3,
          variant: 'neutral',
          text: 'Je wilt voor één jaar zekerheid, terwijl een 3-jarig contract je voor langere tijd vastlegt.',
        },
        dynamic: {
          value: -7.5,
          variant: 'negative',
          text: 'Je wilt elk jaar weten wat je betaalt, terwijl bij een dynamisch contract de prijzen elk uur kunnen veranderen.',
        },
      },
    },
    {
      text: 'Ik vind het prima als mijn energiekosten een paar keer per jaar veranderen. ',
      questionKey: 'variableMultipleTimes',
      answer: {
        variable: {
          value: 7.5,
          variant: 'positive',
          text: 'Je vindt het prima als de kosten een paar keer per jaar veranderen met kans op lagere en hogere prijzen. Perfect voor een variabel contract.',
        },
        fixed1Year: {
          value: 0,
          variant: 'neutral',
          text: 'Je wilt kunnen profiteren van prijsdalingen, maar bij een vast contract voor 1 jaar kan dat maar 1 keer per jaar.',
        },
        fixed3Years: {
          value: -1.5,
          variant: 'negative',
          text: 'Je wilt een aantal keer per jaar kunnen profiteren van prijsdalingen, dat is niet mogelijk bij een 3-jarig vast contract.',
        },
        dynamic: {
          value: -4.5,
          variant: 'negative',
          text: 'Je vindt het prima als je kosten een paar keer per jaar veranderen, terwijl dynamische tarieven elk uur kunnen veranderen.',
        },
      },
    },
    {
      text: 'Het maakt me niet uit dat mijn energiekosten elke maand anders zijn. ',
      questionKey: 'noOpinion',
      answer: {
        variable: {
          value: 0,
          variant: 'neutral',
          text: 'Het maakt je niet uit dat je energiekosten elke maand anders zijn. Bij een variabel contract veranderen de energiekosten maximaal 4 keer per jaar.',
        },
        fixed1Year: {
          value: -4.5,
          variant: 'negative',
          text: 'Je wilt meteen kunnen profiteren van lage prijzen, dat kan niet bij een vast contract.',
        },
        fixed3Years: {
          value: -7.5,
          variant: 'negative',
          text: 'Het maakt je niet uit dat je energiekosten elke maand anders zijn, maar een vast contract voor 3 jaar biedt geen flexibiliteit.',
        },
        dynamic: {
          value: 7.5,
          variant: 'positive',
          text: 'Het maakt je niet uit dat je energiekosten elke maand anders zijn en je wilt meteen profiteren van lage prijzen. Perfect voor een dynamisch contract.',
        },
      },
    },
  ],
};

const QUESTION_VARIABLE_COSTS: RetentionQuestion = {
  key: 'variableCosts',
  text: 'Hoe goed kun je wisselende maandkosten opvangen?',
  kind: 'singleChoice',
  options: [
    {
      text: 'Heel goed',
      subtitle:
        'Ik kan makkelijk wisselende kosten betalen, dat past goed binnen mijn budget.',
      questionKey: 'veryGood',
      answer: {
        variable: {
          value: 0,
          variant: 'neutral',
          text: 'Je kunt heel goed wisselende kosten opvangen. Daardoor vind je het geen probleem dat de prijzen van een variabel contract elk kwartaal kunnen stijgen.',
        },
        fixed1Year: {
          value: 0,
          variant: 'neutral',
          text: 'Je kunt heel goed wisselende kosten opvangen, waardoor de zekerheid van een vast contract voor jou niet per se nodig is.',
        },
        fixed3Years: {
          value: 0,
          variant: 'neutral',
          text: 'Je kunt heel goed wisselende kosten opvangen, waardoor de zekerheid van een vast contract voor jou niet per se nodig is.',
        },
        dynamic: {
          value: 3,
          variant: 'positive',
          text: 'Je kunt heel goed wisselende kosten opvangen. Daardoor kun je goed omgaan met de dagelijkse prijsschommelingen van een dynamisch contract.',
        },
      },
    },
    {
      text: 'Goed',
      subtitle: 'Kleine veranderingen in maandkosten kan ik goed opvangen.',
      questionKey: 'good',
      answer: {
        variable: {
          value: 1,
          variant: 'neutral',
          text: 'Je kunt goed wisselende kosten opvangen. Daardoor vind je het geen probleem dat de prijzen van een variabel contract elk kwartaal kunnen stijgen.',
        },
        fixed1Year: {
          value: 0,
          variant: 'neutral',
          text: 'Je kunt wisselende kosten goed opvangen, toch geeft een vast contract voor 1 jaar extra zekerheid.',
        },
        fixed3Years: {
          value: 0,
          variant: 'neutral',
          text: 'Je kunt wisselende kosten goed opvangen, toch geeft een vast contract voor 3 jaar extra zekerheid.',
        },
        dynamic: {
          value: 2,
          variant: 'neutral',
          text: 'Je kunt kleine veranderingen in de wisselende kosten van een dynamisch contract goed opvangen.',
        },
      },
    },
    {
      text: 'Een beetje',
      subtitle: 'Een beetje verschil in maandkosten is prima voor mij.',
      questionKey: 'reasonable',
      answer: {
        variable: {
          value: 1,
          variant: 'neutral',
          text: 'Je kunt goed wisselende kosten opvangen. Daardoor vind je het geen probleem dat de prijzen van een variabel contract elk kwartaal kunnen stijgen.',
        },
        fixed1Year: {
          value: 1,
          variant: 'positive',
          text: 'Je kunt kleine veranderingen in prijs aan, maar de zekerheid van een contract voor 1 jaar past beter bij jou.',
        },
        fixed3Years: {
          value: 0,
          variant: 'positive',
          text: 'Je kunt kleine veranderingen in prijs aan, maar de zekerheid van een contract voor 3 jaar past beter bij jou.',
        },
        dynamic: {
          value: 0,
          variant: 'neutral',
          text: 'Je kunt kleine veranderingen in de wisselende kosten van een dynamisch contract een beetje opvangen.',
        },
      },
    },
    {
      text: 'Niet goed',
      subtitle:
        'Het is soms wat uitdagend om grotere veranderingen in kosten op te vangen.',
      questionKey: 'bad',
      answer: {
        variable: {
          value: -1,
          variant: 'negative',
          text: 'Je kunt goed wisselende kosten niet zo goed opvangen. Een variabel contract is daardoor een te groot risico.',
        },
        fixed1Year: {
          value: 3,
          variant: 'positive',
          text: 'Een vast contract voor 1 jaar geeft jou de zekerheid die je zoekt voor het aankomende jaar.',
        },
        fixed3Years: {
          value: 2,
          variant: 'positive',
          text: 'Een vast contract voor 3 jaar geeft jou de zekerheid die je zoekt voor het aankomende jaar.',
        },
        dynamic: {
          value: -3,
          variant: 'negative',
          text: 'De kans op grote prijsveranderingen van een dynamisch contract passen niet zo goed bij jouw financiële ruimte.',
        },
      },
    },
    {
      text: 'Helemaal niet',
      subtitle:
        'Ik houd liever mijn kosten stabiel om binnen mijn budget te blijven.',
      questionKey: 'veryBad',
      answer: {
        variable: {
          value: -2,
          variant: 'negative',
          text: 'Je houdt je kosten liever stabiel om zo binnen je budget te blijven. De wisselende prijzen van een variabel contract zijn een te groot risico.',
        },
        fixed1Year: {
          value: 3,
          variant: 'positive',
          text: 'Een vast contract voor 1 jaar geeft jou de zekerheid die je zoekt voor het aankomende jaar.',
        },
        fixed3Years: {
          value: 4,
          variant: 'positive',
          text: 'Een vast contract voor 3 jaar geeft jou de zekerheid die je zoekt voor het aankomende jaar.',
        },
        dynamic: {
          value: -4,
          variant: 'negative',
          text: 'Je houdt je kosten liever stabiel om zo binnen je budget te blijven. De prijsschommelingen van een dynamisch contract zijn een te groot risico.',
        },
      },
    },
  ],
};

const QUESTION_CHANGE_ENERGY_USAGES: RetentionQuestion = {
  key: 'changeEnergyUsages',
  text: 'Kun je je energieverbruik aanpassen aan goedkope momenten op de dag?',
  kind: 'singleChoice',
  options: [
    {
      text: 'Ja',
      subtitle:
        'Ik kan mijn apparaten aanzetten wanneer de stroom goedkoper is.',
      questionKey: 'yes',
      answer: {
        variable: {
          value: 0,
          variant: 'neutral',
          text: 'Je kunt kiezen wanneer je je apparaten aanzet, maar dat heeft geen invloed op je energiekosten. De tarieven van een variabel contract blijven minstens elk kwartaal hetzelfde.',
        },
        fixed1Year: {
          value: 0,
          variant: 'neutral',
          text: 'Je kunt kiezen wanneer je je apparaten aanzet, maar dat heeft geen invloed op je energiekosten. De tarieven van een vast contract blijven hetzelfde.',
        },
        fixed3Years: {
          value: 0,
          variant: 'neutral',
          text: 'Je kunt kiezen wanneer je je apparaten aanzet, maar dat heeft geen invloed op je energiekosten. De tarieven van een vast contract blijven hetzelfde.',
        },
        dynamic: {
          value: 3,
          variant: 'positive',
          text: 'Je kunt je apparaten aanzetten op de goedkope momenten. Ideaal voor een dynamisch contract.',
        },
      },
    },
    {
      text: 'Soms',
      subtitle:
        'Ik kan mijn energieverbruik een beetje sturen wanneer de stroomprijs goedkoop is.',
      questionKey: 'sometimes',
      answer: {
        variable: {
          value: 0,
          variant: 'neutral',
          text: 'Je kunt soms kiezen wanneer je je apparaten aanzet, maar dat heeft geen invloed op je energiekosten. De prijs is elk minstens elk kwartaal hetzelfde.',
        },
        fixed1Year: {
          value: 0,
          variant: 'neutral',
          text: 'Je kunt soms kiezen wanneer je je apparaten aanzet, maar dat heeft geen invloed op je energiekosten. De tarieven van een vast contract blijven hetzelfde.',
        },
        fixed3Years: {
          value: 0,
          variant: 'neutral',
          text: 'Je kunt soms kiezen wanneer je je apparaten aanzet, maar dat heeft geen invloed op je energiekosten. De tarieven van een vast contract blijven hetzelfde.',
        },
        dynamic: {
          value: 2,
          variant: 'neutral',
          text: 'Je kunt je energieverbruik soms aanpassen aan de goedkope momenten. Een dynamisch contract kan dan voordelig zijn.',
        },
      },
    },
    {
      text: 'Nee',
      subtitle:
        'Ik gebruik energie wanneer ik het nodig heb en kan geen rekening houden met de energieprijs.',
      questionKey: 'no',
      answer: {
        variable: {
          value: 2,
          variant: 'neutral',
          text: 'Je gebruikt je energie wanneer jij dat nodig hebt, maar dat heeft geen invloed op de energiekosten van een variabel contract.',
        },
        fixed1Year: {
          value: 3,
          variant: 'positive',
          text: 'Je gebruikt je energie wanneer jij dat nodig hebt. Perfect voor een vast contract.',
        },
        fixed3Years: {
          value: 3,
          variant: 'positive',
          text: 'Je gebruikt je energie wanneer jij dat nodig hebt. Perfect voor een vast contract.',
        },
        dynamic: {
          value: -3,
          variant: 'negative',
          text: 'Je gebruikt je energie wanneer jij dat nodig hebt en je kunt geen rekening houden met de energieprijs. Dan past een dynamisch contract niet bij jou.',
        },
      },
    },
  ],
};

const QUESTION_CHANGE_ENERGY_USAGE_DESIRED: RetentionQuestion = {
  key: 'changeEnergyUsageDesired',
  text: 'Wil je je energieverbruik aanpassen aan goedkope momenten op de dag?',
  kind: 'singleChoice',
  options: [
    {
      text: 'Ja',
      subtitle:
        'Ik wil dagelijks energieprijzen controleren en mijn verbruik aanpassen. Bijvoorbeeld de wasmachine aanzetten op het goedkoopste moment van de dag.',
      questionKey: 'yes',
      answer: {
        variable: {
          value: 0,
          variant: 'neutral',
          text: 'Je wilt elke dag je energieverbruik actief aanpassen aan de goedkope momenten om te besparen. Maar bij een variabel contract levert dit geen voordeel op.',
        },
        fixed1Year: {
          value: -1,
          variant: 'negative',
          text: 'Je wilt elke dag je energieverbruik actief aanpassen aan de goedkope momenten om te besparen. Maar bij een vast contract levert dit geen voordeel op.',
        },
        fixed3Years: {
          value: -2,
          variant: 'negative',
          text: 'Je wilt elke dag je energieverbruik actief aanpassen aan de goedkope momenten om te besparen. Maar bij een vast contract levert dit geen voordeel op.',
        },
        dynamic: {
          value: 2,
          variant: 'positive',
          text: 'Je wilt elke dag je energieverbruik actief aanpassen aan de goedkope momenten om te besparen. Perfect voor een dynamisch contract.',
        },
      },
    },
    {
      text: 'Soms',
      subtitle:
        'Maar niet elke dag. Bijvoorbeeld door altijd in het weekend de wasmachine aan te zetten.',
      questionKey: 'sometimes',
      answer: {
        variable: {
          value: 1,
          variant: 'neutral',
          text: 'Je wilt soms rekening houden met de energieprijzen. Maar bij een variabel contract levert dit geen voordeel op.',
        },
        fixed1Year: {
          value: 0,
          variant: 'neutral',
          text: 'Je wilt soms rekening houden met de energieprijzen. Maar bij een vast contract levert dit geen voordeel op. ',
        },
        fixed3Years: {
          value: 0,
          variant: 'neutral',
          text: 'Je wilt soms rekening houden met de energieprijzen. Maar bij een vast contract levert dit geen voordeel op.',
        },
        dynamic: {
          value: 1,
          variant: 'neutral',
          text: 'Je wilt soms rekening houden met de energieprijzen. Dat past bij een dynamisch contract. ',
        },
      },
    },
    {
      text: 'Nee',
      subtitle:
        'Ik wil niet nadenken over de energieprijs en gebruik mijn apparaten wanneer dat nodig is.',
      questionKey: 'no',
      answer: {
        variable: {
          value: -1,
          variant: 'neutral',
          text: 'Je wilt niet nadenken over je energieverbruik en de energieprijzen. Dat past prima bij een variabel contract.',
        },
        fixed1Year: {
          value: 1,
          variant: 'positive',
          text: 'Je wilt niet nadenken over je energieverbruik en de energieprijzen. Perfect voor een vast contract.',
        },
        fixed3Years: {
          value: 2,
          variant: 'positive',
          text: 'Je wilt niet nadenken over je energieverbruik en de energieprijzen. Perfect voor een vast contract.',
        },
        dynamic: {
          value: -2,
          variant: 'negative',
          text: 'Je wilt niet nadenken over energieprijzen en wanneer je je apparaten gebruikt. Dat past niet bij een dynamisch contract.',
        },
      },
    },
  ],
};

export const QUESTION_APPLIANCES: RetentionQuestion = {
  key: 'appliances',
  text: 'Welke apparaten heb je?',
  kind: 'multipart',
  options: [
    {
      text: 'Zonnepanelen',
      questionKey: 'solarPanels',
      answer: {
        variable: {
          value: 0,
          variant: 'neutral',
          text: 'Je hebt een of meerdere duurzame installaties die kunnen profiteren van wisselende prijzen, maar bij een variabel contract profiteer je daar niet optimaal van.',
        },
        fixed1Year: {
          value: 0,
          variant: 'neutral',
          text: 'Je hebt een of meerdere duurzame installaties die kunnen profiteren van wisselende prijzen, maar dat is niet mogelijk met een vast contract.',
        },
        fixed3Years: {
          value: 0,
          variant: 'neutral',
          text: 'Je hebt een of meerdere duurzame installaties die kunnen profiteren van wisselende prijzen, maar dat is niet mogelijk met een vast contract.',
        },
        dynamic: {
          value: 1,
          variant: 'neutral',
          text: 'Je hebt een of meerdere duurzame installaties waarmee je kunt profiteren op goedkope momenten.',
        },
      },
    },
    {
      text: 'Warmtepomp',
      questionKey: 'heatPump',
      answer: {
        variable: {
          value: 1,
          variant: 'neutral',
          text: 'Je hebt een of meerdere duurzame installaties die kunnen profiteren van wisselende prijzen, maar bij een variabel contract profiteer je daar niet optimaal van.',
        },
        fixed1Year: {
          value: -1,
          variant: 'neutral',
          text: 'Je hebt een of meerdere duurzame installaties die kunnen profiteren van wisselende prijzen, maar dat is niet mogelijk met een vast contract.',
        },
        fixed3Years: {
          value: -1,
          variant: 'neutral',
          text: 'Je hebt een of meerdere duurzame installaties die kunnen profiteren van wisselende prijzen, maar dat is niet mogelijk met een vast contract.',
        },
        dynamic: {
          value: 1,
          variant: 'neutral',
          text: 'Je hebt een of meerdere duurzame installaties waarmee je kunt profiteren op goedkope momenten.',
        },
      },
    },
    {
      text: 'Thuisbatterij',
      questionKey: 'homeBattery',
      answer: {
        variable: {
          value: 1,
          variant: 'neutral',
          text: 'Je hebt duurzame installaties die kunnen profiteren van wisselende prijzen, maar bij een variabel contract profiteer je daar niet optimaal van.',
        },
        fixed1Year: {
          value: -1,
          variant: 'negative',
          text: 'Je hebt duurzame installaties die kunnen profiteren van wisselende prijzen, maar dat is niet mogelijk met een vast contract.',
        },
        fixed3Years: {
          value: -1,
          variant: 'negative',
          text: 'Je hebt duurzame installaties die kunnen profiteren van wisselende prijzen, maar dat is niet mogelijk met een vast contract.',
        },
        dynamic: {
          value: 2,
          variant: 'positive',
          text: 'Je hebt veel duurzame installaties waarmee je optimaal kunt profiteren op goedkope momenten.',
        },
      },
    },
    {
      text: 'Laadpaal voor mijn elektrische auto',
      questionKey: 'chargingStation',
      answer: {
        variable: {
          value: 1,
          variant: 'neutral',
          text: 'Je hebt duurzame installaties die kunnen profiteren van wisselende prijzen, maar bij een variabel contract profiteer je daar niet optimaal van.',
        },
        fixed1Year: {
          value: -1,
          variant: 'negative',
          text: 'Je hebt duurzame installaties die kunnen profiteren van wisselende prijzen, maar dat is niet mogelijk met een vast contract.',
        },
        fixed3Years: {
          value: -1,
          variant: 'negative',
          text: 'Je hebt duurzame installaties die kunnen profiteren van wisselende prijzen, maar dat is niet mogelijk met een vast contract.',
        },
        dynamic: {
          value: 2,
          variant: 'positive',
          text: 'Je hebt veel duurzame installaties waarmee je optimaal kunt profiteren op goedkope momenten.',
        },
      },
    },
    {
      text: 'Ik heb geen van deze apparaten',
      questionKey: 'noAppliances',
      answer: {
        variable: {
          value: 0,
          variant: 'neutral',
          text: 'Omdat je geen duurzame installaties hebt, is een variabel contract een prima keuze.',
        },
        fixed1Year: {
          value: 1,
          variant: 'positive',
          text: 'Omdat je geen duurzame installaties hebt, is een vast contract een goede keuze.',
        },
        fixed3Years: {
          value: 1,
          variant: 'positive',
          text: 'Omdat je geen duurzame installaties hebt, is een vast contract een goede keuze.',
        },
        dynamic: {
          value: -2,
          variant: 'negative',
          text: 'Zonder duurzame installaties is het lastiger om optimaal te profiteren van dynamische tarieven.',
        },
      },
    },
  ],
};

export const QUESTIONS: RetentionQuestion[] = [
  QUESTION_ENERGY_COSTS,
  QUESTION_VARIABLE_COSTS,
  QUESTION_CHANGE_ENERGY_USAGES,
  QUESTION_CHANGE_ENERGY_USAGE_DESIRED,
  QUESTION_APPLIANCES,
];

/**
 * for now, we only consider singleChoice and multipart questions (multipleChoice is not used in the questionnaire, but is of similar type to singleChoice)
 * @param currentAnswers
 */
export function interviewQuestions(currentAnswers: Answer[]): Question[] {
  return QUESTIONS.map(({ kind, key, text, options }) => {
    const prefilledAnswer: string =
      (currentAnswers.find((answer) =>
        options.find((opt) => opt.questionKey === answer.questionKey)
      )?.value as string) ?? options[0].questionKey;

    // we need to concat the key to make sure that we have unique keys for each question
    const subQuestions = options.map(
      (option) =>
        ({
          kind: 'binary',
          key: `${key}_${option.questionKey}`,
          text: option.text,
        }) as BinaryQuestion
    );

    if (kind === 'singleChoice' || kind === 'multipleChoice') {
      return {
        key,
        kind,
        text,
        answerSpecification: {
          suggestion: `${key}_${prefilledAnswer}`,
        },
        options: options.map((option) => ({
          answer: `${key}_${option.questionKey}`,
          text: option.text,
          explanation: option.subtitle,
        })),
      } as SingleChoiceQuestion | MultipleChoiceQuestion;
    }

    if (kind === 'multipart') {
      return {
        key,
        kind,
        text,
        ...(kind === 'multipart' && {
          subQuestions,
        }),
      } as MultipartQuestion;
    }

    return {
      key,
      kind,
      text,
    };
  });
}

/**
 * calculated from the answers above, hardcoded for now to avoid overhead
 */
export const MIN_MAX_SCORE_PER_SPECIFIC_VALUE: {
  [key in QuestionnaireOfferValues]: {
    min: number;
    max: number;
  };
} = {
  // min: -6 + -2 + 0 + -1 + (0) = -9; max: 7.5 + 1 + 2 + 1 + (1 + 1 + 1) = 14.5
  variable: { min: -9, max: 14.5 },
  // min: -4.5 + 0 + 0 + -1 + (-1 + -1 + -1) = -8.5; max: 7.5 + 3 + 3 + 1 + (1) = 15.5
  fixed1Year: { min: -8.5, max: 15.5 },
  // min: -7.5 + 0 + 0 + -2 + (-1 + -1 + -1) = -12.5; max: 7.5 + 4 + 3 + 2 + (1) = 17.5
  fixed3Years: { min: -12.5, max: 17.5 },
  // min: -7.5 + -4 + -3 + -2 + (-2) = -18.5; max: 7.5 + 3 + 3 + 2 + (2 + 2) = 19.5
  dynamic: { min: -18.5, max: 19.5 },
};

/**
 * The matching algorithm, calculates the matching percentage based on the score and the variant
 * @param score the total score from filling in the questionnaire
 * @param variant the variant of the offer to calculate the matching percentage for
 */
export function calculateMatchingPercentage({
  score,
  variant,
}: {
  score: number;
  variant: QuestionnaireOfferValues;
}) {
  const { min: minimumScore, max: maximumScore } =
    MIN_MAX_SCORE_PER_SPECIFIC_VALUE[variant];

  return (score - minimumScore) / (maximumScore - minimumScore);
}

/**
 * Calculates the highest matching percentage for the given score across all variants
 * @param scores the total score from filling in the questionnaire
 */
export function calculateAndReturnHighestMatch(
  scores: Record<QuestionnaireOfferValues, number>
) {
  // we define the input scores list as an object of OfferSpecificValues and their value, so we can safely assert its type
  const matchingPercentages = Object.entries(scores).map(
    ([variant, score]) => ({
      variant: variant as QuestionnaireOfferValues,
      percentage: calculateMatchingPercentage({
        score,
        variant: variant as QuestionnaireOfferValues,
      }),
    })
  );

  const highestMatch = matchingPercentages.reduce<{
    variant: QuestionnaireOfferValues;
    percentage: number;
  }>((acc, curr) => (acc.percentage > curr.percentage ? acc : curr), {
    variant: 'variable',
    percentage: 0,
  });

  return {
    highestMatch: highestMatch.variant,
    ...matchingPercentages.reduce<Record<QuestionnaireOfferValues, number>>(
      (acc, curr) => {
        acc[curr.variant] = curr.percentage;
        return acc;
      },
      { variable: 0, fixed1Year: 0, fixed3Years: 0, dynamic: 0 }
    ),
  };
}

/**
 * map the answers returned from the questionnaire to values from QUESTIONS, and calculates the score based on the above functions
 * @param answers
 */
export function calculateScoresFromAnswers(answers: Answer[]) {
  const scores = QUESTIONS.reduce(
    (acc, curr) => {
      const currentAnswers = curr.options.filter((currentOption) =>
        answers.find((answer) =>
          answer.questionKey === curr.key && curr.kind === 'multipart'
            ? true
            : answer.value === `${curr.key}_${currentOption.questionKey}`
        )
      );

      if (curr.kind === 'multipart') {
        const multipartAnswerValues = currentAnswers.filter((currentAnswer) =>
          answers.find(
            (answer) =>
              (answer.value as Record<string, boolean>)[
                `${curr.key}_${currentAnswer.questionKey}`
              ]
          )
        );

        if (multipartAnswerValues.length === 0) {
          return acc; // no answers for multipart question
        } else {
          return multipartAnswerValues.reduce(
            (subAcc, opt) => ({
              variable: subAcc.variable + (opt.answer.variable.value ?? 0),
              fixed1Year:
                subAcc.fixed1Year + (opt.answer.fixed1Year.value ?? 0),
              fixed3Years:
                subAcc.fixed3Years + (opt.answer.fixed3Years.value ?? 0),
              dynamic: subAcc.dynamic + (opt.answer.dynamic.value ?? 0),
            }),
            acc
          );
        }
      } else {
        const answer = currentAnswers[0];
        return {
          variable: acc.variable + (answer?.answer.variable.value ?? 0),
          fixed1Year: acc.fixed1Year + (answer?.answer.fixed1Year.value ?? 0),
          fixed3Years:
            acc.fixed3Years + (answer?.answer.fixed3Years.value ?? 0),
          dynamic: acc.dynamic + (answer?.answer.dynamic.value ?? 0),
        };
      }
    },
    {
      variable: 0,
      fixed1Year: 0,
      fixed3Years: 0,
      dynamic: 0,
    }
  );

  return calculateAndReturnHighestMatch(scores);
}

// this is a copy of only relevant fields from `{ CommodityOfferContent } from '@innogy/commodity-offers'`, separated due to circular dependencies.
export interface QuestionnaireResultsContent {
  questionnaireBestChoiceTitle?: string;
  questionnaireVariableCardTitle?: string;
  questionnaireVariableCardDescription?: string;
  questionnaireExplanationButtonTitle?: string;
  questionnaireVariableCardSubmitTitle?: string;
  questionnaireDynamicCardDescription?: string;
  questionnaire1YearCardDescription?: string;
  questionnaire3YearsCardDescription?: string;
  questionnaireAdviceModalTitle?: string;
  questionnaireVariableModalTitle?: string;
  questionnaireVariableModalDescription?: string;
  questionnaireVariableModalSubmitTitle?: string;
  questionnaireVariableModalCancelTitle?: string;
  questionnaireAlternateChoiceTitle?: string;
  questionnaireVariableToSuccesUrl?: { href: string };
}
